/**
 * @file utils/index.ts
 * @description 通用工具函数集合，包括时间格式化、加解密、颜色处理等
 */
import CryptoJS from 'crypto-js'
import dayjs from './time'


/**
 * 日期格式类型定义
 */
type DateType = 'yyyy-mm-dd' | 'yyyy'| 'yyyy-mm' | 'yyyy-mm-dd hh:mm:ss' | 'hh:mm:ss'

// ==================== AES 加密配置 ====================
/**
 * AES 加密密钥（16位）
 */
const KEY = CryptoJS.enc.Utf8.parse('@hxcloud@hxcloud')
/**
 * AES 加密向量（16位）
 */
const IV = CryptoJS.enc.Utf8.parse('@dahua2023@dahua')

export default {
  /**
   * 数字前置补零
   * @description 将数字补齐到指定长度，不足的前面补零
   * @param num - 需要补零的数字或字符串
   * @param length - 目标长度，默认为 2
   * @returns 补零后的字符串
   * @example fullZero(5) => '05', fullZero(123, 5) => '00123'
   */
  fullZero: (num: any, length: number = 2) => {
    return ('' + num).padStart(length, '0')
  },

  /**
   * 根据时区格式化时间
   * @description 将时间转换为用户时区的格式化字符串
   * @param date - 需要格式化的时间（时间戳或时间字符串）
   * @param format - 输出格式，默认 'Y-M-D H:M:S'
   * @param timeZone - 时区，默认使用用户时区
   * @returns 格式化后的时间字符串
   */
  timeZoneFormat: (date, format='Y-M-D H:M:S', timeZone = null) => {
    if (!date) {
      return "--";
    }
    // 判断传入的date是时间对象还是时间戳
    const dateObj = new Date(date);

    let [year, month, day, hour, minutes, seconds] = [
      dateObj.getFullYear(),
      (dateObj.getMonth() + 1) >= 10 ? (dateObj.getMonth() + 1) : '0' + (dateObj.getMonth() + 1),
      dateObj.getDate() >= 10 ? dateObj.getDate() : '0' + dateObj.getDate(),
      dateObj.getHours() >= 10 ? dateObj.getHours() : '0' + dateObj.getHours(),
      dateObj.getMinutes() >= 10 ? dateObj.getMinutes() : '0' + dateObj.getMinutes(),
      dateObj.getSeconds() >= 10 ? dateObj.getSeconds() : '0' + dateObj.getSeconds()
    ]
    switch (format) {
      case 'Y-M-D H:M:S':
        return `${year}-${month}-${day} ${hour}:${minutes}:${seconds}`
      case 'Y-M-D':
        return `${year}-${month}-${day}`
      case 'Y.M.D':
        return `${year}.${month}.${day}`
      case '年-月-日 H:M:S':
        return `${parseInt(year)}${Vue.$t('client.year')}${parseInt(month)}${Vue.$t('client.month')}${parseInt(day)}${Vue.$t('client.day')} ${hour}:${minutes}:${seconds}`
      case `年-月-日`:
        return `${parseInt(year)}${Vue.$t('client.year')}${parseInt(month)}${Vue.$t('client.month')}${parseInt(day)}${Vue.$t('client.day')}`
      case 'H:M:S':
        return hour + ':' + minutes + ':' + seconds
    }
  },

  /**
   * 通过 dayjs 获取剩余时间
   * @description 计算两个时间戳之间的剩余时间（年、月、日）
   * @param futureTimestamp - 结束时间戳
   * @param startTimestamp - 开始时间戳，默认当前时间
   * @returns 剩余时间对象，包含 years、months、days 属性
   */
  getRemainingTimeByLib: (futureTimestamp, startTimestamp) => {
    const now = startTimestamp ? dayjs(startTimestamp) : dayjs()
    const target = dayjs(futureTimestamp)
    const diffTime = target.diff(now);
    if (diffTime <= 0) return null;

    // 1. 先算相差的【整年】
    const years = target.diff(now, 'year');

    // 2. 将当前时间推后【整年】，作为计算月份的基点
    const dateAfterYears = now.add(years, 'year');

    // 3. 算剩下的【整月】
    const months = target.diff(dateAfterYears, 'month');

    // 4. 将时间再推后【整月】，作为计算天数的基点
    const dateAfterMonths = dateAfterYears.add(months, 'month');

    // 5. 剩下的就是【天数】
    const days = target.diff(dateAfterMonths, 'day');

    return { years, months, days };
  },

  /**
   * 通过dayjs格式化时间
   * @description 将时间转换为用户时区的格式化字符串
   * @param timestamp - 需要格式化的时间（时间戳或时间字符串）
   * @param format - 输出格式，默认 'Y-M-D H:M:S'
   * @param timeZone - 时区，默认使用用户时区
   * @returns 格式化后的时间字符串
   */
  timeFormatDate: (timestamp, timeZone = null, format='YYYY-MM-DD HH:mm:ss') => {
    if (!timestamp) {
      return "--";
    }
    return timeZone ? dayjs(timestamp).tz(timeZone).format(format) : dayjs(timestamp).format(format);
  },

  /**
   * 通过dayjs格式化时间戳
   * @description 将时间转换为用户时区的时间戳
   * @param userSelectStr - 需要格式化的时间字符串
   * @param timeZone - 时区，默认使用用户时区
   * @returns 格式化后的时间字符串
   */
  dateFormatTime: (userSelectStr, timeZone = null) => {
    if (!userSelectStr) {
      return "--";
    }
    return timeZone ? dayjs.tz(userSelectStr, timeZone).valueOf() : dayjs(userSelectStr).valueOf()
  },

  /**
   * 通用时间格式化
   * @description 将时间戳或时间对象转换为指定格式的字符串
   * @param timeNum - 时间戳或可解析的时间值
   * @param type - 输出格式类型，默认 'yyyy-mm-dd hh:mm:ss'
   * @returns 格式化后的时间字符串，无效时返回空字符串
   */
  formatDate (timeNum: any, type: DateType = 'yyyy-mm-dd hh:mm:ss') {
    if (!timeNum) return ''
    const time: any = {}
    let date = new Date(timeNum)
    time.y = this.fullZero(date.getFullYear())
    time.m = this.fullZero(date.getMonth() + 1)
    time.d = this.fullZero(date.getDate())
    time.h = this.fullZero(date.getHours())
    time.min = this.fullZero(date.getMinutes())
    time.s = this.fullZero(date.getSeconds())
    let dateStr = ''
    switch (type) {
      case "yyyy-mm-dd hh:mm:ss":
        dateStr = `${time.y}-${time.m}-${time.d} ${time.h}:${time.min}:${time.s}`
        break
      case "yyyy-mm-dd":
        dateStr = `${time.y}-${time.m}-${time.d}`
        break
      case "yyyy":
        dateStr = `${time.y}`
        break
      case "yyyy-mm":
        dateStr = `${time.y}-${time.m}`
        break
      case "hh:mm:ss":
        dateStr = `${time.h}:${time.min}:${time.s}`
        break
      default:
        dateStr = ''
    }
    return dateStr
  },
  /**
   * 判断字符串是否为有效的 JSON
   * @description 检查字符串是否可以解析为 JSON 对象
   * @param str - 需要检查的字符串
   * @returns true 表示是有效 JSON，false 表示不是
   */
  isJson(str: any) {
    if (typeof str == "string") {
      try {
        let obj = JSON.parse(str)
        return !!(typeof obj == "object" && obj)
      } catch (e) {
        return false
      }
    }
    return false
  },
  /**
   * AES 加密
   * @description 使用 AES-CBC 模式加密字符串
   * @param word - 需要加密的字符串
   * @param keyStr - 自定义密钥（可选）
   * @param ivStr - 自定义向量（可选）
   * @returns Base64 编码的加密字符串
   */
  encrypt (word: any, keyStr?: any, ivStr?: any) {
    let key = KEY
    let iv = IV
    if (keyStr) {
      key = CryptoJS.enc.Utf8.parse(keyStr)
      iv = CryptoJS.enc.Utf8.parse(ivStr)
    }
    let srcs = CryptoJS.enc.Utf8.parse(word)
    let encrypted = CryptoJS.AES.encrypt(srcs, key, {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.ZeroPadding
    })
    return CryptoJS.enc.Base64.stringify(encrypted.ciphertext)
  },
  /**
   * AES 解密
   * @description 使用 AES-CBC 模式解密字符串
   * @param word - 需要解密的 Base64 字符串
   * @param keyStr - 自定义密钥（可选）
   * @param ivStr - 自定义向量（可选）
   * @returns 解密后的原始字符串
   */
  decrypt (word: any, keyStr?: any, ivStr?: any) {
    let key = KEY
    let iv = IV

    if (keyStr) {
      key = CryptoJS.enc.Utf8.parse(keyStr)
      iv = CryptoJS.enc.Utf8.parse(ivStr)
    }

    let base64 = CryptoJS.enc.Base64.parse(word)
    let src = CryptoJS.enc.Base64.stringify(base64)

    let decrypt = CryptoJS.AES.decrypt(src, key, {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.ZeroPadding
    })

    let decryptedStr = decrypt.toString(CryptoJS.enc.Utf8)
    return decryptedStr.toString()
  },

  /**
   * 设置 CSS 变量
   * @description 设置指定 DOM 元素的 CSS 自定义属性
   * @param prop - CSS 变量名（如 '--color-primary'）
   * @param val - 变量值
   * @param dom - 目标 DOM 元素，默认为 document.documentElement
   */
  setProperty(prop: string, val: any, dom = document.documentElement) {
    dom.style.setProperty(prop, val)
  },

  /**
   * 获取 CSS 变量值
   * @description 获取指定 DOM 元素的 CSS 自定义属性值
   * @param prop - CSS 变量名
   * @param dom - 目标 DOM 元素，默认为 document.documentElement
   * @returns CSS 变量的当前值
   */
  getProperty(prop: string, dom = document.documentElement) {
    // 使用 getComputedStyle 获取所有来源的变量值
    return getComputedStyle(dom).getPropertyValue(prop).trim();
  },
  /**
   * 混合两种颜色
   * @description 按指定权重混合两个十六进制颜色
   * @param color1 - 第一个颜色值（十六进制，如 '#ffffff'）
   * @param color2 - 第二个颜色值
   * @param weight - 混合权重（0-1），默认 0.5 表示各占 50%
   * @returns 混合后的颜色值（十六进制）
   * @example mix('#ffffff', '#000000', 0.5) => '#808080'
   */
  mix(color1: string, color2: string, weight: number = 0.5): string {
    let color = '#'; // 结果颜色初始化
    // 循环处理 R/G/B 三个通道
    for (let i = 0; i <= 2; i++) {
      // 提取 color1 的通道分量（十六进制 → 十进制）
      const c1 = parseInt(color1.substring(1 + i * 2, 3 + i * 2), 16);
      // 提取 color2 的通道分量
      const c2 = parseInt(color2.substring(1 + i * 2, 3 + i * 2), 16);
      // 计算混合值（线性插值 + 四舍五入）
      const c = Math.round(c1 * weight + c2 * (1 - weight));
      // 十进制 → 十六进制，补零成两位并拼接到结果
      color += c.toString(16).padStart(2, '0');
    }
    return color; // 返回混合颜色
  },
}
